<template>
  <section
    v-if="aiChatVisible"
    class="fixed inset-y-0 right-0 z-40 mt-16 flex max-w-full overscroll-none"
    aria-labelledby="ai-chat-heading"
  >
    <Transition
      enter-active-class="transition ease-in-out duration-500 sm:duration-500"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition ease-in-out duration-500 sm:duration-500"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div
        v-show="isOpen"
        ref="panelRef"
        class="relative w-screen"
        :style="{ maxWidth: `${panelWidth}px` }"
      >
        <!-- Resize handle -->
        <div
          class="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize bg-transparent transition-colors hover:bg-purple-300"
          @mousedown="startResize"
        ></div>

        <div
          class="flex h-full flex-col overflow-hidden border-y border-l border-gray-200 bg-white"
        >
          <!-- Header -->
          <div
            class="flex flex-row items-center justify-between border-b border-gray-200 p-4 sm:px-6"
          >
            <div class="flex items-center gap-2">
              <FyrAiLogo :size="20" class="text-purple-600" />
              <h2
                id="ai-chat-heading"
                class="text-lg font-semibold text-burgundy-900"
              >
                Automagic AI
              </h2>
            </div>
            <div v-if="phase === 'chat'" class="flex items-center gap-1">
              <div class="relative" data-history-dropdown>
                <button
                  v-tooltip="'Chat history'"
                  class="rounded-md p-1 text-gray-400 hover:text-gray-600"
                  aria-haspopup="true"
                  :aria-expanded="showHistory"
                  @click="showHistory = !showHistory"
                >
                  <span class="sr-only">Chat history</span>
                  <Icon name="list-ul" class="size-4" />
                </button>
                <!-- History dropdown -->
                <div
                  v-if="showHistory"
                  role="menu"
                  class="absolute right-0 z-50 mt-1 w-72 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                >
                  <p class="px-3 py-1.5 text-xs font-semibold text-gray-500">
                    Recent chats
                  </p>
                  <div class="max-h-64 overflow-y-auto">
                    <button
                      v-for="s in recentSessions"
                      :key="s.id"
                      class="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-gray-50"
                      :class="{
                        'bg-purple-50': s.id === sessionId,
                      }"
                      @click="switchSession(s.id)"
                    >
                      <Icon
                        name="comment-dots"
                        class="size-3.5 shrink-0 text-gray-400"
                      />
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-sm text-gray-700">
                          {{ s.title }}
                        </p>
                        <p class="text-xs text-gray-400">
                          {{ formatRelativeTime(s.modifiedAt) }}
                        </p>
                      </div>
                    </button>
                    <p
                      v-if="recentSessions.length === 0"
                      class="px-3 py-2 text-xs text-gray-400"
                    >
                      No previous chats
                    </p>
                  </div>
                </div>
              </div>
              <button
                v-tooltip="'New session'"
                class="rounded-md p-1 text-gray-400 hover:text-gray-600"
                @click="restart"
              >
                <span class="sr-only">New session</span>
                <Icon name="redo" class="size-4" />
              </button>
            </div>
          </div>

          <!-- Consent pending message -->
          <div
            v-if="aiChatConsentPending"
            class="flex flex-1 flex-col items-center justify-center p-6 text-center"
          >
            <Icon name="lock" class="size-10 text-gray-400" />
            <p class="mt-4 text-sm font-medium text-gray-900">
              Consent Required
            </p>
            <p class="mt-2 text-sm text-gray-600">
              A manager must give consent before Automagic AI can be used.
            </p>
            <RouterLink
              :to="{ name: 'consent_settings' }"
              class="mt-4 text-sm font-medium text-purple-600 hover:text-purple-800"
            >
              Go to Consent Settings
            </RouterLink>
          </div>

          <!-- Content -->
          <template v-else>
            <AIPromptPanel
              v-if="phase === 'prompt'"
              @session-created="handleSessionCreated"
            />
            <AIChatPanel
              v-else-if="sessionId !== null"
              v-model:initial-message="pendingInitialMessage"
              v-model:initial-attachments="pendingInitialAttachments"
              :session-id="sessionId"
            />
          </template>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import Icon from "@/components/Core/Icon.vue";
import FyrAiLogo from "@/components/Core/FyrAiLogo.vue";
import AIPromptPanel from "@/components/AIAgent/AIPromptPanel.vue";
import AIChatPanel from "@/components/AIAgent/AIChatPanel.vue";
import { useAIChatSlideover } from "@/composables/useAIChatSlideover";
import type { ChatAttachment } from "@/services/ai_agent";
import { useAISessionsQuery } from "@/services/ai_agent";
import { DateTime } from "luxon";

const {
  isOpen,
  phase,
  sessionId,
  aiChatVisible,
  aiChatConsentPending,
  panelWidth,
  isResizing,
  pendingInitialMessage,
  restart,
} = useAIChatSlideover();

const pendingInitialAttachments = ref<ChatAttachment[] | undefined>();
const sessions = useAISessionsQuery();
const showHistory = ref(false);
const panelRef = ref<HTMLElement | null>(null);
let stopActiveResize: (() => void) | null = null;

const MAX_RECENT = 10;

const recentSessions = computed(() => {
  if (!sessions.data) return [];
  return sessions.data.slice(0, MAX_RECENT);
});

function formatRelativeTime(timestamp: string): string {
  return DateTime.fromISO(timestamp).toRelative() ?? "";
}

function handleSessionCreated(
  id: number,
  prompt?: string,
  attachments?: ChatAttachment[],
): void {
  pendingInitialMessage.value = prompt ?? null;
  pendingInitialAttachments.value = attachments;
  sessionId.value = id;
  phase.value = "chat";
}

function switchSession(id: number): void {
  showHistory.value = false;
  handleSessionCreated(id);
}

function onClickOutsideHistory(e: MouseEvent): void {
  if (showHistory.value) {
    const target = e.target as HTMLElement;
    if (!target.closest("[data-history-dropdown]")) {
      showHistory.value = false;
    }
  }
}

onMounted(() => document.addEventListener("click", onClickOutsideHistory));
onBeforeUnmount(() => {
  document.removeEventListener("click", onClickOutsideHistory);
  stopActiveResize?.();
});

const MIN_WIDTH = 384;
const MAX_WIDTH = 960;

function startResize(event: MouseEvent): void {
  event.preventDefault();
  const startX = event.clientX;
  const startWidth = panelRef.value?.offsetWidth ?? panelWidth.value;
  isResizing.value = true;

  function onMouseMove(e: MouseEvent): void {
    const delta = startX - e.clientX;
    panelWidth.value = Math.min(
      MAX_WIDTH,
      Math.max(MIN_WIDTH, startWidth + delta),
    );
  }

  stopActiveResize = () => {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    isResizing.value = false;
    stopActiveResize = null;
  };

  function onMouseUp(): void {
    stopActiveResize?.();
  }

  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
}
</script>
